import { useMemo } from 'react'
import type { MemberDocument } from '@/lib/db/rxdb/schemas/member'
import { useMembers as useRxDBMembers } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'

/**
 * Hook for accessing workspace members from Electric sync.
 * Members are synced from the server and filtered to current workspace.
 *
 * @returns Object containing workspace members and loading state
 *
 * @example
 * ```tsx
 * function MemberList() {
 *   const { users, isLoading } = useMembers()
 *
 *   if (isLoading) return <Spinner />
 *
 *   return users.map(user => (
 *     <UserCard key={user.id} user={user} />
 *   ))
 * }
 * ```
 */
export function useMembers() {
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const { members, isLoading } = useRxDBMembers()

  const users = useMemo<Array<MemberDocument>>(() => {
    // Filter out deleted users and ensure workspace match
    // (server-side filtering should handle most of this, but double-check for safety)
    return members.filter((user) => {
      if (workspaceId && user.envId !== workspaceId) {
        return false
      }

      if (user.isDeleted) {
        return false
      }

      return true
    })
  }, [members, workspaceId])

  return {
    users,
    isLoading,
  }
}

/**
 * Hook for mention autocomplete suggestions.
 * Filters workspace members by search query against display name, email, first/last name.
 *
 * @param query - Search query string (case-insensitive)
 * @param limit - Maximum number of results (default: 20)
 * @returns Filtered and sorted user suggestions
 *
 * @example
 * ```tsx
 * function MentionPlugin() {
 *   const [search, setSearch] = useState('')
 *   const suggestions = useMentionSuggestions(search, 10)
 *
 *   return (
 *     <MentionDropdown>
 *       {suggestions.map(user => (
 *         <MentionOption key={user.id} user={user} />
 *       ))}
 *     </MentionDropdown>
 *   )
 * }
 * ```
 */
export function useMentionSuggestions(
  query: string,
  limit = 20,
): Array<MemberDocument> {
  const { users } = useMembers()

  return useMemo(() => {
    if (!query || query.trim().length === 0) {
      // No search query - return all users sorted by name
      return users
        .sort((a, b) => {
          const nameA = a.name || a.email || ''
          const nameB = b.name || b.email || ''
          return nameA.localeCompare(nameB)
        })
        .slice(0, limit)
    }

    const searchLower = query.toLowerCase().trim()

    // Filter users by search query (case-insensitive)
    const filtered = users.filter((user) => {
      const name = (user.name || '').toLowerCase()
      const email = (user.email || '').toLowerCase()

      return name.includes(searchLower) || email.includes(searchLower)
    })

    // Sort by relevance: exact prefix matches first, then contains
    const sorted = filtered.sort((a, b) => {
      const aName = (a.name || a.email || '').toLowerCase()
      const bName = (b.name || b.email || '').toLowerCase()

      const aStartsWith = aName.startsWith(searchLower)
      const bStartsWith = bName.startsWith(searchLower)

      // Prioritize prefix matches
      if (aStartsWith && !bStartsWith) return -1
      if (!aStartsWith && bStartsWith) return 1

      // Alphabetical within same priority level
      return aName.localeCompare(bName)
    })

    return sorted.slice(0, limit)
  }, [users, query, limit])
}

/**
 * Hook to get a single member by internal database user ID.
 * Useful for displaying member info in comments, activities, etc.
 *
 * @param userId - Internal database user ID to look up
 * @returns Object containing member (if found) and loading state
 *
 * @example
 * ```tsx
 * function CommentAuthor({ authorId }: { authorId: string }) {
 *   const { member, isLoading } = useMember(authorId)
 *
 *   if (isLoading) return <Skeleton />
 *
 *   return member ? (
 *     <UserAvatar name={member.name} email={member.email} />
 *   ) : (
 *     <span>Unknown User</span>
 *   )
 * }
 * ```
 */
export function useMember(
  userId: string | null | undefined,
): { member: MemberDocument | null; isLoading: boolean } {
  const { users, isLoading } = useMembers()

  const member = useMemo(() => {
    if (!userId) return null
    // Look up by internal userId (NOT exUserId - that's for backend mapping only)
    return users.find((user) => user.userId === userId) ?? null
  }, [users, userId])

  return { member, isLoading }
}

// Legacy exports removed - use useMembers and useMember instead
